import { useEffect, useState } from "react";
import Module from "@/components/module/Module";
import Head from "next/head";
import styles from "./index.module.scss";
import { useAtomValue, useSetAtom } from "jotai";
import { stockTradingItemIdState, textColorState } from "@/lib/store";
import { NextPage } from "next";
import { classNames } from "@/lib/utils";
import { MemoizedDataGrid } from "@/components/data-grid/DataGrid";
import { getDataGridNews } from "@/controllers/news";
import { newsIdOf, openNewsStory } from "@/components/news-story-modal/NewsStoryModal";
import ContentLayout from "@/layouts/content/ContentLayout";
import { Stock } from "@/models/stock";
import { buildStockOgImage } from "@/lib/stock-og";
import { stockPageGetServerSideProps } from "@/lib/stock-page-ssr";
import { stockDescriptions, stockTitle } from "@/lib/stock-seo";
import { stockCanonicalUrl } from "@/lib/stock-url";

const newsSearchParams = { min_impact: 2, type: "universe" as "universe", show_source: true, stockFilter: true };

const StockNewsPage: NextPage<{ stock: Stock }> = ({ stock }) => {
    const textColor = useAtomValue(textColorState);

    const [newsSearchFilters, setNewsSearchFilters] = useState(null);

    const setStockTradingItemId = useSetAtom(stockTradingItemIdState);

    useEffect(() => {
        stock ? setStockTradingItemId(stock.tradingitem_id) : setStockTradingItemId(null);
    }, [stock]);

    const getNewsDetail = (row) => newsIdOf(row) && openNewsStory(newsIdOf(row));

    return (
        <>
            {stock &&
                (() => {
                    const { ogImageUrl, cardTitle } = buildStockOgImage(stock, "News");
                    return (
                        <Head>
                            <title>{stockTitle(stock, "news")}</title>
                            <link rel="canonical" href={stockCanonicalUrl(stock, "news")} />
                            <meta name="keywords" content={`${stock.name}, ${stock.exchangesymbol}:${stock.ticker}, News, Stock News`} />
                            <meta name="description" content={stockDescriptions.news(stock)} />
                            <meta name="twitter:card" content="summary_large_image" />
                            <meta name="twitter:title" content={stockTitle(stock, "news")} />
                            <meta name="twitter:description" content={stockDescriptions.news(stock)} />
                            <meta name="twitter:url" content={stockCanonicalUrl(stock, "news")} />
                            <meta name="twitter:image" content={ogImageUrl} />
                            <meta name="twitter:image:alt" content={cardTitle} />
                            <meta property="og:title" content={stockTitle(stock, "news")} />
                            <meta property="og:description" content={stockDescriptions.news(stock)} />
                            <meta property="og:url" content={stockCanonicalUrl(stock, "news")} />
                            <meta property="og:image" content={ogImageUrl} key="og:image" />
                            <meta property="og:image:width" content="1200" />
                            <meta property="og:image:height" content="630" />
                            <meta property="og:image:alt" content={cardTitle} />
                        </Head>
                    );
                })()}
            {stock && (
                <ContentLayout help={{ moduleId: "news_search", modulePage: "V2_news" }} filters={newsSearchFilters} title="News" titleAs="h2" allowFullscreen feedback>
                    <div className={classNames(styles.NewsPage, styles["full-page"])}>
                        <style jsx>{`
                            .${styles["news-selector"]} button {
                                color: ${textColor};
                            }
                            @media (hover: hover) {
                                .${styles.filters} > button:hover {
                                    background-color: grey;
                                }
                            }
                        `}</style>
                        <div className={styles.modules} style={{ width: "100%", minWidth: "100%" }}>
                            <div className={styles["news-search"]}>
                                <Module noHeader>
                                    <MemoizedDataGrid id={stock.company_id} fetcher={getDataGridNews} data={newsSearchParams} onRowClick={getNewsDetail} setFilters={setNewsSearchFilters} hiddenColumns={["stock"]} isNewsSearch />
                                </Module>
                            </div>
                        </div>
                    </div>
                </ContentLayout>
            )}
        </>
    );
};

export const getServerSideProps = stockPageGetServerSideProps({ tab: "news", allowedTypes: [1] });

export default StockNewsPage;
